import { categoryMessageLog } from '@helpers/combat/combat-log';
import {
  TRADESKILL_MAX_LEVEL,
  TRADESKILL_QUEUE_BASE_SIZE,
  TRADESKILL_QUEUE_LEVELS_PER_SLOT,
  TRADESKILL_QUEUE_MAX_SIZE,
  TRADESKILL_XP_END,
  TRADESKILL_XP_START,
  XP_CURVE_EASE,
} from '@helpers/config';
import { getEntriesByType, getEntry } from '@helpers/content/content';
import { defaultTradeskillBuilding } from '@helpers/defaults';
import {
  analyticsSafeSegment,
  analyticsSendDesignEvent,
} from '@helpers/engine/analytics';
import { roundToNearest10 } from '@helpers/engine/number';
import { isCollectibleDiscovered } from '@helpers/item/collectibles';
import {
  globalEffectSumsState,
  tradeskillsState,
  updateGamestate,
} from '@helpers/state-game';
import { taskEventTradeskillLevel } from '@helpers/task/task-events';
import type {
  DifficultyTier,
  GameState,
  GameStateTradeskills,
  RecipeContent,
  Tradeskill,
  TradeskillBuildingState,
  TradeskillContent,
  TradeskillId,
  TradeskillLevelRequirementContent,
} from '@interfaces';
import { ALL_TRADESKILLS } from '@interfaces';

const DEFAULT_BUILDING = defaultTradeskillBuilding();

// Never throws - content may not be loaded yet on early renders (e.g. a
// returning player reopens a tradeskill screen before content finishes its
// async load). Callers must tolerate `undefined` gracefully; the underlying
// lookup is reactive, so once content loads any `computed()` that read this
// resolves correctly on its own.
export function tradeskillIdForName(
  tradeskill: Tradeskill,
): TradeskillId | undefined {
  return getEntry<TradeskillContent>(tradeskill)?.id;
}

export function tradeskillNameForId(id: TradeskillId): Tradeskill | undefined {
  return getEntry<TradeskillContent>(id)?.name as Tradeskill | undefined;
}

// Eases from 10 XP at level 1 to 5,000 at the cap; progress**1.5 keeps early-level jumps gentle.
export function tradeskillXpForLevel(level: number): number {
  const progress = (level - 1) / (TRADESKILL_MAX_LEVEL - 1);
  const xp =
    TRADESKILL_XP_START +
    (TRADESKILL_XP_END - TRADESKILL_XP_START) * progress ** XP_CURVE_EASE;
  return roundToNearest10(xp);
}

// Boosts from collectibles/buffs still count toward the overall cap.
export function tradeskillMaxQueueSize(
  level: number,
  tradeskill: Tradeskill,
): number {
  const tradeskillId = tradeskillIdForName(tradeskill);
  const boost = tradeskillId
    ? (globalEffectSumsState().tradeskillQueueSizeBoosts[tradeskillId] ?? 0)
    : 0;
  return Math.min(
    TRADESKILL_QUEUE_MAX_SIZE,
    TRADESKILL_QUEUE_BASE_SIZE +
      Math.floor(level / TRADESKILL_QUEUE_LEVELS_PER_SLOT) +
      boost,
  );
}

export function tradeskillBuilding(
  tradeskill: Tradeskill,
): TradeskillBuildingState {
  const id = tradeskillIdForName(tradeskill);
  if (!id) return DEFAULT_BUILDING;
  return tradeskillsState()[id] ?? DEFAULT_BUILDING;
}

// For `updateGamestate` callbacks - they must read the draft `state` they
// were handed, not `gamestate()`.
export function tradeskillBuildingIn(
  state: GameState,
  tradeskillId: TradeskillId,
): TradeskillBuildingState {
  return state.tradeskills[tradeskillId] ?? DEFAULT_BUILDING;
}

function levelRequirementFor(
  tradeskill: Tradeskill,
  level: number,
): TradeskillLevelRequirementContent | undefined {
  const tradeskillId = tradeskillIdForName(tradeskill);
  return getEntriesByType<TradeskillLevelRequirementContent>(
    'tradeskilllevelrequirement',
  ).find(
    (entry) => entry.tradeskillId === tradeskillId && entry.level === level,
  );
}

export function tradeskillLevelGateSatisfied(
  tradeskill: Tradeskill,
  level: number,
): boolean {
  const requirement = levelRequirementFor(tradeskill, level);
  if (!requirement) return true;

  return isCollectibleDiscovered(requirement.requiredCollectibleId);
}

// The requirement blocking the next level, or undefined once satisfied.
export function tradeskillActiveGate(
  tradeskill: Tradeskill,
): TradeskillLevelRequirementContent | undefined {
  const nextLevel = tradeskillBuilding(tradeskill).level + 1;
  if (tradeskillLevelGateSatisfied(tradeskill, nextLevel)) return undefined;

  return levelRequirementFor(tradeskill, nextLevel);
}

// Gated by tradeskillLevelGateSatisfied - XP holds at the cap (no loss) until the gate clears.
function tradeskillLeveledUp(
  building: TradeskillBuildingState,
  tradeskill: Tradeskill,
  amount: number,
): TradeskillBuildingState {
  let level = building.level;
  let current = building.xp.current + amount;
  let maximum = building.xp.maximum;

  while (
    level < TRADESKILL_MAX_LEVEL &&
    current >= maximum &&
    tradeskillLevelGateSatisfied(tradeskill, level + 1)
  ) {
    current -= maximum;
    level += 1;
    maximum = tradeskillXpForLevel(level);
  }

  if (current > maximum) current = maximum;

  return { ...building, level, xp: { current, maximum } };
}

// Rescales xp.maximum to the current curve, clamping current down if needed. Never forces a level-up itself.
export function retrofitTradeskillXp(
  tradeskills: GameStateTradeskills,
): GameStateTradeskills {
  const retrofitted = { ...tradeskills };

  (Object.keys(retrofitted) as TradeskillId[]).forEach((tradeskillId) => {
    const building = retrofitted[tradeskillId];
    const maximum = tradeskillXpForLevel(building.level);

    retrofitted[tradeskillId] = {
      ...building,
      xp: { current: Math.min(building.xp.current, maximum), maximum },
    };
  });

  return retrofitted;
}

export function tradeskillGainXp(tradeskill: Tradeskill, amount: number): void {
  if (amount <= 0) return;

  const tradeskillId = tradeskillIdForName(tradeskill);
  if (!tradeskillId) return;

  const previousLevel = tradeskillBuilding(tradeskill).level;
  let newLevel = previousLevel;

  updateGamestate((state) => {
    state.tradeskills[tradeskillId] = tradeskillLeveledUp(
      tradeskillBuildingIn(state, tradeskillId),
      tradeskill,
      amount,
    );
    newLevel = state.tradeskills[tradeskillId].level;
    return state;
  });

  if (newLevel > previousLevel) {
    categoryMessageLog(
      'Craft',
      tradeskill,
      `**${tradeskill}** reached level ${newLevel}!`,
    );
    analyticsSendDesignEvent(
      `Kingdom:Building:LevelUp:${analyticsSafeSegment(tradeskill)}`,
      newLevel,
    );
    void taskEventTradeskillLevel(tradeskillId, newLevel);
  }
}

export function migrateTradeskillStateKeys(
  tradeskills: Record<string, TradeskillBuildingState>,
): GameStateTradeskills {
  const remapped = {} as GameStateTradeskills;

  Object.entries(tradeskills ?? {}).forEach(([key, building]) => {
    const isLegacyName = (ALL_TRADESKILLS as string[]).includes(key);
    const id = isLegacyName
      ? tradeskillIdForName(key as Tradeskill)
      : (key as TradeskillId);
    if (id) remapped[id] = building;
  });

  getEntriesByType<TradeskillContent>('tradeskill').forEach((content) => {
    remapped[content.id] ??= DEFAULT_BUILDING;
  });

  return remapped;
}

export function craftXpChance(
  recipe: RecipeContent,
  buildingLevel: number,
): number {
  const { minTradeskillLevel: min, maxTradeskillLevel: max } = recipe;
  if (max <= min) return buildingLevel <= min ? 100 : 0;

  const progress = (buildingLevel - min) / (max - min + 1);
  if (progress >= 1) return 0;
  if (progress >= 0.75) return 25;
  if (progress >= 0.5) return 50;
  if (progress >= 0.25) return 50;
  return 100;
}

export function craftXpChanceTier(
  recipe: RecipeContent,
  buildingLevel: number,
): DifficultyTier {
  const chance = craftXpChance(recipe, buildingLevel);

  if (chance >= 100) return 'Hard';
  if (chance >= 50) return 'Medium';
  if (chance >= 25) return 'Easy';
  return 'Trivial';
}
